import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Play, Pause, Volume2, VolumeX, Maximize, Loader2, Gauge } from 'lucide-react';
import { mediaService } from '../../services/media';
import { videoTelemetry } from '../../services/telemetry';
import { useAuth } from '../../context/AuthContext';

interface WatermarkData {
  name: string;
  email: string;
  uid: string;
}

// Watermark timing/position logic (matches server-side ffmpeg behavior)
// Can be overridden by WASM module if loaded
interface WatermarkCalc {
  isVisible: (t: number) => boolean;
  getX: (t: number, W: number, tw: number) => number;
  getY: (t: number, H: number, th: number) => number;
}

// Time the player waits on a stalled video element before swapping to the
// fallback proxy URL. Lowered from 10s to give mainland-China viewers a
// faster recovery path -- the EC2 proxy is preferable to a frozen player.
const STALL_TO_FALLBACK_MS = 5000;
// Once already on the fallback proxy, how long to wait before arming the
// fallback_failed telemetry event.
const STALL_AFTER_FALLBACK_MS = 5000;

// Per-session random salt so each playback has different positions
const sessionSalt = Math.floor(Math.random() * 100000);

// Deterministic pseudo-random from seed (0..1)
function seededRandom(seed: number): number {
  const x = Math.sin((seed + sessionSalt) * 9301 + 49297) * 233280;
  return x - Math.floor(x);
}

const jsWatermarkCalc: WatermarkCalc = {
  isVisible: (t: number) => (t % 45) < 10,
  getX: (t: number, W: number, tw: number) => {
    const cycle = Math.floor(t / 45);
    const margin = W * 0.03;
    return margin + seededRandom(cycle * 2) * (W - tw - margin * 2);
  },
  getY: (t: number, H: number, th: number) => {
    const cycle = Math.floor(t / 45);
    const margin = H * 0.05;
    return margin + seededRandom(cycle * 2 + 1) * (H - th - margin * 2);
  },
};

// Forensic watermark: scatter user ID at very low opacity across the frame
function drawForensicWatermark(
  ctx: CanvasRenderingContext2D,
  uid: string,
  W: number,
  H: number
) {
  ctx.save();
  ctx.font = '10px sans-serif';
  ctx.fillStyle = 'rgba(255,255,255,0.015)';

  // Deterministic scatter based on uid hash
  let hash = 0;
  for (let i = 0; i < uid.length; i++) {
    hash = ((hash << 5) - hash + uid.charCodeAt(i)) | 0;
  }

  const count = 12;
  for (let i = 0; i < count; i++) {
    const seed = hash + i * 7919;
    const x = ((seed * 2654435761) >>> 0) % W;
    const y = ((seed * 2246822519) >>> 0) % H;
    const angle = ((seed * 3266489917) >>> 0) % 360;
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate((angle * Math.PI) / 180);
    ctx.fillText(uid, 0, 0);
    ctx.restore();
  }
  ctx.restore();
}

function getCalc(): WatermarkCalc {
  return jsWatermarkCalc;
}

interface VideoPlayerProps {
  src?: string;
  mediaId?: string;
  poster?: string;
  onProgress?: (progress: number) => void;
  onComplete?: () => void;
  className?: string;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  publicVideo?: boolean;
}

export default function VideoPlayer({
  src,
  mediaId,
  poster,
  onProgress,
  onComplete,
  className = '',
  autoPlay = false,
  loop = false,
  muted: mutedProp = false,
  publicVideo = false,
}: VideoPlayerProps) {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const authUserIdRef = useRef<string>(user?._id || '');
  authUserIdRef.current = user?._id || '';
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const animFrameRef = useRef<number>(0);

  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [isMuted, setIsMuted] = useState(mutedProp);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showControls, setShowControls] = useState(!autoPlay);
  const [playbackRate, setPlaybackRate] = useState(() => {
    const saved = localStorage.getItem('videoPlaybackRate');
    return saved ? parseFloat(saved) : 1;
  });
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
  const speedMenuRef = useRef<HTMLDivElement>(null);
  const playbackRateRef = useRef(playbackRate);
  const serverDuration = useRef(0);
  const watermarkRef = useRef<WatermarkData | null>(null);
  const useCanvasRendering = useRef(false);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fallbackUrlRef = useRef<string | null>(null);
  // Signed URL of the baseline-encoded compat rendition (videos-compat/...).
  // The player swaps to this on MediaError 4 (format error) — the same-bytes
  // proxy at fallbackUrlRef can't recover a decode failure, only a network
  // failure. Null when the media has no compat file (legacy uploads).
  const compatUrlRef = useRef<string | null>(null);
  // Signed compat rendition on the Aliyun DCDN host (videos-compat/ via OSS).
  // For mainland-CN viewers this is the *primary* source (guaranteed-compatible
  // Baseline@L3.1 + bitrate-capped) and the in-Aliyun MediaError-4 fallback, so
  // recovery never leaves DCDN for the slow CloudFront/S3 path. Null off the CN
  // path or when the media has no compat file.
  const compatCnUrlRef = useRef<string | null>(null);
  // Server-side IP geo (e.g. 'CN'); gates the DCDN-preferring source selection.
  const regionRef = useRef<string>('');
  // Mirror of the currently-loaded resolvedUrl, readable inside callbacks
  // (triggerFallback) so we never re-issue the source we're already playing.
  const resolvedUrlRef = useRef<string | null>(null);
  const fallbackTriedRef = useRef(false);
  // Which fallback path was taken — included in fallback_failed / recovery
  // telemetry so the dashboard can split "proxy saved us" from "compat saved
  // us" (DCDN or CloudFront) without parsing currentSrc.
  const fallbackPathRef = useRef<'proxy' | 'compat' | 'compat-cn' | null>(null);
  // True after we re-issued the proxy URL once following a second stall.
  // One transparent retry recovers transient mid-stream blips before we
  // declare fallback_failed.
  const proxyRetriedRef = useRef(false);
  const resumeTimeRef = useRef(0);
  const stallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stallStartedAtRef = useRef<number | null>(null);
  const reportedRecoveryRef = useRef(false);
  const startedEmittedRef = useRef(false);
  // Negotiated HTTP version (h2/h3/http/1.1) for the currently-loaded src.
  // Captured once PerformanceResourceTiming surfaces the video request and
  // attached to every subsequent telemetry event for the same source.
  const httpProtocolRef = useRef<string>('');
  const mediaIdRef = useRef(mediaId);
  mediaIdRef.current = mediaId;
  const publicVideoRef = useRef(publicVideo);
  publicVideoRef.current = publicVideo;

  const emitTelemetry = useCallback((
    partial: Omit<Parameters<typeof videoTelemetry.record>[0], 'mediaId' | 'isPublic' | 'userId'> & {
      mediaId?: string;
    }
  ) => {
    const id = partial.mediaId || mediaIdRef.current;
    if (!id) return;
    videoTelemetry.record({
      ...partial,
      mediaId: id,
      userId: authUserIdRef.current || undefined,
      isPublic: !!publicVideoRef.current,
      httpProtocol: partial.httpProtocol ?? (httpProtocolRef.current || undefined),
    });
  }, []);
  const onProgressRef = useRef(onProgress);
  onProgressRef.current = onProgress;
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const clearStallTimer = useCallback(() => {
    if (stallTimerRef.current) {
      clearTimeout(stallTimerRef.current);
      stallTimerRef.current = null;
    }
  }, []);

  // Capture <video> state at emit time so the dashboard can tell apart
  // "decoder never saw bytes" (videoWidth=0, readyState=0) from "decoder
  // produced frames then errored" (videoWidth>0, readyState>=2). The host
  // distinguishes which CDN/origin served the bytes that failed without
  // requiring URL parsing on the dashboard side.
  const diagnosticSnapshot = useCallback((): {
    videoWidth: number;
    videoHeight: number;
    readyState: number;
    networkState: number;
    currentSrcHost: string;
    fallbackPath: string;
  } => {
    const v = videoRef.current;
    if (!v) {
      return { videoWidth: 0, videoHeight: 0, readyState: 0, networkState: 0, currentSrcHost: '', fallbackPath: fallbackPathRef.current || '' };
    }
    let host = '';
    try { host = v.currentSrc ? new URL(v.currentSrc).hostname : ''; } catch { /* ignore */ }
    return {
      videoWidth: v.videoWidth || 0,
      videoHeight: v.videoHeight || 0,
      readyState: v.readyState || 0,
      networkState: v.networkState || 0,
      currentSrcHost: host,
      fallbackPath: fallbackPathRef.current || '',
    };
  }, []);

  // Swap to a recovery URL on stall or playback error. Preserves position.
  //
  // Hop order: primary → compat (on MediaError 4) → proxy. The EC2 proxy is
  // always the terminal hop: it is served from the app's own origin, so it
  // still works when a CDN hostname is unreachable (expired TLS cert, DNS,
  // blocked edge). Browsers report such load failures as MediaError 4 too,
  // indistinguishable from a decode error, so a failed compat hop — or a
  // MediaError 4 with no compat at all — still gets one proxy attempt.
  const triggerFallback = useCallback((reason: string, errorCode?: number): boolean => {
    // Already on the terminal hop (or there is none) — nothing left to try.
    if (fallbackPathRef.current === 'proxy') return false;
    if (fallbackTriedRef.current && !fallbackUrlRef.current) return false;
    const onCompatHop = fallbackTriedRef.current;

    const hasAnyCompat = !!compatUrlRef.current || !!compatCnUrlRef.current;
    if (!onCompatHop && reason === 'error' && errorCode === 4 && !hasAnyCompat) {
      const video = videoRef.current;
      const positionSec = video && isFinite(video.currentTime) ? video.currentTime : 0;
      emitTelemetry({
        eventType: 'compat_missing',
        reason: 'error',
        errorCode: 4,
        positionSec,
        viaFallback: false,
        ...diagnosticSnapshot(),
      });
      // Fall through to the proxy: a CDN load failure is also MediaError 4.
    }

    // MediaError 4 on the primary → recover with a compat rendition; the
    // same-bytes proxy can't fix a decode error. Pick a compat source
    // *different* from the one we're already on (for CN the primary already is
    // compatCnUrl), preferring the DCDN compat for mainland viewers so recovery
    // stays inside Aliyun's network. Anything else (stall, or a failed compat
    // hop) → the proxy.
    let targetUrl: string | null = null;
    let path: 'proxy' | 'compat' | 'compat-cn' = 'proxy';
    if (!onCompatHop && reason === 'error' && errorCode === 4 && hasAnyCompat) {
      const current = resolvedUrlRef.current;
      const cnCompat = regionRef.current === 'CN' ? compatCnUrlRef.current : null;
      if (cnCompat && cnCompat !== current) {
        targetUrl = cnCompat;
        path = 'compat-cn';
      } else if (compatUrlRef.current && compatUrlRef.current !== current) {
        targetUrl = compatUrlRef.current;
        path = 'compat';
      }
    }
    if (!targetUrl) {
      targetUrl = fallbackUrlRef.current;
      path = 'proxy';
    }
    if (!targetUrl) return false;
    fallbackTriedRef.current = true;
    fallbackPathRef.current = path;

    const video = videoRef.current;
    const positionSec = video && isFinite(video.currentTime) ? video.currentTime : 0;
    if (video && isFinite(video.currentTime) && video.currentTime > 0) {
      resumeTimeRef.current = video.currentTime;
    }
    // New source — protocol negotiation will differ from the previous URL.
    // Clear so handleLoadedMetadata re-samples nextHopProtocol.
    httpProtocolRef.current = '';
    clearStallTimer();
    console.warn('[VideoPlayer] falling back', { reason, errorCode, path, mediaId: mediaIdRef.current });
    emitTelemetry({
      eventType: 'fallback_triggered',
      reason: reason === 'stall' || reason === 'error' ? reason : '',
      errorCode: errorCode || 0,
      positionSec,
      viaFallback: false,
      ...diagnosticSnapshot(),
    });
    setResolvedUrl(targetUrl);
    return true;
  }, [clearStallTimer, emitTelemetry, diagnosticSnapshot]);

  const hideControlsTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch short-TTL signed URL, auto-refresh before expiry
  const fetchSignedUrl = useCallback((isRefresh = false) => {
    if (!mediaId) return;

    let cancelled = false;

    if (!isRefresh) {
      setResolvedUrl(null);
      setError(null);
      setErrorDetail(null);
      setIsLoading(true);
      setCurrentTime(0);
      setDuration(0);
      setBuffered(0);
      setIsPlaying(false);
      setShowControls(true);
      serverDuration.current = 0;
      watermarkRef.current = null;
      useCanvasRendering.current = false;
      fallbackUrlRef.current = null;
      compatUrlRef.current = null;
      compatCnUrlRef.current = null;
      regionRef.current = '';
      fallbackTriedRef.current = false;
      fallbackPathRef.current = null;
      proxyRetriedRef.current = false;
      resumeTimeRef.current = 0;
      stallStartedAtRef.current = null;
      reportedRecoveryRef.current = false;
      startedEmittedRef.current = false;
      httpProtocolRef.current = '';
      clearStallTimer();
    }

    const urlPromise = publicVideo
      ? mediaService.getPublicVideoUrl(mediaId)
      : mediaService.getSignedVideoUrl(mediaId);

    urlPromise.then(({ url, duration: dur, expiresIn, ...rest }) => {
      if (cancelled) return;

      if (!isRefresh) {
        serverDuration.current = dur;
        if (dur > 0) setDuration(dur);
      }
      const watermark = 'watermark' in rest ? (rest as any).watermark : undefined;
      if (watermark) {
        watermarkRef.current = watermark;
        useCanvasRendering.current = true;
      }
      const fallbackUrl = 'fallbackUrl' in rest ? (rest as any).fallbackUrl : undefined;
      fallbackUrlRef.current = typeof fallbackUrl === 'string' ? fallbackUrl : null;
      const compatUrl = 'compatUrl' in rest ? (rest as any).compatUrl : undefined;
      compatUrlRef.current = typeof compatUrl === 'string' ? compatUrl : null;
      const compatCnUrl = 'compatCnUrl' in rest ? (rest as any).compatCnUrl : null;
      compatCnUrlRef.current = typeof compatCnUrl === 'string' ? compatCnUrl : null;
      const cnUrl = 'cnUrl' in rest ? (rest as any).cnUrl : null;
      const region = 'region' in rest ? (rest as any).region : '';
      regionRef.current = typeof region === 'string' ? region : '';
      if (!isRefresh && !startedEmittedRef.current) {
        startedEmittedRef.current = true;
        emitTelemetry({ eventType: 'playback_started' });
      }
      // Mainland-China viewers (region from server-side IP geo): the DCDN-fronted
      // compat rendition is the primary — guaranteed Baseline@L3.1 + bitrate-capped,
      // so it survives a throttled link and avoids the MediaError-4 decode failure
      // the HD primary trips on strict decoders. Fall back to the HD DCDN URL
      // (cnUrl) for videos that don't have a compat yet, then the CloudFront url.
      // International users are unaffected (region !== 'CN' → url).
      const primary =
        region === 'CN' && typeof compatCnUrl === 'string' && compatCnUrl ? compatCnUrl :
        region === 'CN' && typeof cnUrl === 'string' && cnUrl ? cnUrl :
        url;
      setResolvedUrl(primary);

      // Schedule refresh at 80% of TTL
      if (expiresIn && expiresIn > 0) {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = setTimeout(() => fetchSignedUrl(true), expiresIn * 0.8 * 1000);
      }
    }).catch((err: any) => {
      if (cancelled || isRefresh) return;
      const status: number | undefined = err?.response?.status;
      const serverMsg: string | undefined =
        err?.response?.data?.detail || err?.response?.data?.error;
      let msg: string;
      if (status === 404) msg = t('videoPlayer.notFound');
      else if (status === 403) msg = t('videoPlayer.accessDenied');
      else if (status && status >= 500) msg = t('videoPlayer.serviceUnavailable');
      else if (!status) msg = t('videoPlayer.networkError');
      else msg = t('videoPlayer.failedToLoad');
      setError(msg);
      const statusLabel = status ? String(status) : 'network';
      const detail = t('videoPlayer.errorDetail', { status: statusLabel, mediaId });
      setErrorDetail(serverMsg ? `${detail} — ${serverMsg}` : detail);
      console.warn('[VideoPlayer] token fetch failed', { mediaId, status, serverMsg, err });
    });

    return () => { cancelled = true; };
  }, [mediaId, publicVideo, clearStallTimer, emitTelemetry]);

  useEffect(() => {
    if (!mediaId) {
      setResolvedUrl(null);
      watermarkRef.current = null;
      useCanvasRendering.current = false;
      return;
    }

    const cancel = fetchSignedUrl(false);

    return () => {
      cancel?.();
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [mediaId, fetchSignedUrl]);

  const videoUrl = resolvedUrl || src;

  // Mirror resolvedUrl into a ref so triggerFallback can compare against the
  // currently-loaded source without re-reading state.
  useEffect(() => { resolvedUrlRef.current = resolvedUrl; }, [resolvedUrl]);

  // Canvas render loop for watermark overlay
  useEffect(() => {
    if (!useCanvasRendering.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const renderFrame = () => {
      try {
        // Only draw when the element actually has a current frame. While
        // buffering / seeking / errored (readyState < 2) the canvas keeps the
        // last good frame the browser retains, instead of painting black.
        if (video.readyState >= 2 && !video.ended) {
          drawVideoFrame(ctx, video, canvas);
        }
      } catch { /* keep loop alive */ }
      animFrameRef.current = requestAnimationFrame(renderFrame);
    };

    animFrameRef.current = requestAnimationFrame(renderFrame);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [resolvedUrl]);

  function drawVideoFrame(
    ctx: CanvasRenderingContext2D,
    video: HTMLVideoElement,
    canvas: HTMLCanvasElement
  ) {
    const W = video.videoWidth || canvas.width;
    const H = video.videoHeight || canvas.height;

    if (canvas.width !== W) canvas.width = W;
    if (canvas.height !== H) canvas.height = H;

    ctx.drawImage(video, 0, 0, W, H);

    const wm = watermarkRef.current;
    if (!wm) return;

    const t = video.currentTime;
    const calc = getCalc();

    // Forensic watermark (always drawn)
    drawForensicWatermark(ctx, wm.uid, W, H);

    // Visible watermark (5s every 45s)
    if (calc.isVisible(t)) {
      ctx.save();
      const fontSize = Math.max(12, W * 0.014);
      ctx.font = `${fontSize}px sans-serif`;

      const line1 = `${wm.name} | ${wm.email}`;
      const line2 = `ID: ${wm.uid}`;
      const tw = Math.max(ctx.measureText(line1).width, ctx.measureText(line2).width);
      const th = fontSize * 3;

      const x = calc.getX(t, W, tw);
      const y = calc.getY(t, H, th);

      ctx.translate(x + tw / 2, y + th / 2);
      ctx.rotate((-20 * Math.PI) / 180);
      ctx.translate(-(x + tw / 2), -(y + th / 2));

      // Dark outline: visible on light backgrounds
      ctx.strokeStyle = 'rgba(0,0,0,0.2)';
      ctx.lineWidth = 3;
      ctx.lineJoin = 'round';
      ctx.strokeText(line1, x, y + fontSize);
      ctx.strokeText(line2, x, y + fontSize * 2.2);

      // Light fill: visible on dark backgrounds
      ctx.fillStyle = 'rgba(255,255,255,0.25)';
      ctx.fillText(line1, x, y + fontSize);
      ctx.fillStyle = 'rgba(255,255,255,0.2)';
      ctx.fillText(line2, x, y + fontSize * 2.2);

      ctx.restore();
    }
  }

  // Anti-tamper: watch for video element becoming directly visible
  useEffect(() => {
    if (!useCanvasRendering.current) return;

    const video = videoRef.current;
    if (!video) return;

    const observer = new MutationObserver(() => {
      const style = window.getComputedStyle(video);
      const opacity = parseFloat(style.opacity);
      if (opacity > 0.01 && style.position !== 'absolute') {
        video.pause();
        console.warn('[VideoPlayer] Tamper detected: video element made visible');
      }
    });

    observer.observe(video, { attributes: true, attributeFilter: ['style', 'class'] });

    return () => observer.disconnect();
  }, [resolvedUrl]);

  // Video event listeners
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoUrl) return;

    video.load();

    const getDuration = () => {
      if (serverDuration.current > 0) return serverDuration.current;
      const dur = video.duration;
      return (isFinite(dur) && dur > 0) ? dur : 0;
    };

    const updateBuffered = () => {
      const dur = getDuration();
      if (dur > 0 && video.buffered.length > 0) {
        const end = video.buffered.end(video.buffered.length - 1);
        setBuffered((end / dur) * 100);
      }
    };

    const handleLoadedMetadata = () => {
      const dur = getDuration();
      if (dur > 0) setDuration(dur);
      video.playbackRate = playbackRateRef.current;
      setIsLoading(false);
      // After a fallback swap, resume from where CloudFront stalled.
      if (resumeTimeRef.current > 0) {
        const resumeAt = resumeTimeRef.current;
        resumeTimeRef.current = 0;
        try { video.currentTime = resumeAt; } catch { /* ignore */ }
      }
      // Sample the negotiated HTTP version once the browser has actually
      // fetched the media. h3/h2 has very different reliability across the
      // GFW; pinning the value here lets us correlate stalls per protocol.
      if (!httpProtocolRef.current) {
        try {
          const src = video.currentSrc;
          if (src) {
            const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
            for (let i = entries.length - 1; i >= 0; i--) {
              if (entries[i].name === src && entries[i].nextHopProtocol) {
                httpProtocolRef.current = entries[i].nextHopProtocol;
                break;
              }
            }
          }
        } catch { /* performance API not available — leave empty */ }
      }
    };

    const handleDurationChange = () => {
      if (serverDuration.current <= 0) {
        const dur = video.duration;
        if (isFinite(dur) && dur > 0) setDuration(dur);
      }
    };

    const handleTimeUpdate = () => {
      const actual = video.currentTime;
      setCurrentTime(actual);
      updateBuffered();
      const dur = getDuration();
      if (dur > 0) {
        onProgressRef.current?.((actual / dur) * 100);
      }
    };

    const handleSeeking = () => {
      // A seek started (user click on the bar, or the post-fallback resume-seek).
      // Cancel any pending stall->fallback countdown so seek-buffering isn't
      // misclassified as a CDN stall — reloading the src mid-seek is exactly what
      // freezes playback ("stuck after clicking the progress bar").
      clearStallTimer();
    };
    const handleSeeked = () => {
      // Completed seek also clears the countdown (it may have been armed before
      // video.seeking flipped true). A genuine stall re-arms via the next waiting.
      clearStallTimer();
      // Force canvas redraw after seek completes
      if (useCanvasRendering.current && canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        if (ctx) {
          try { drawVideoFrame(ctx, video, canvasRef.current); } catch { /* ignore */ }
        }
      }
    };
    const handleProgress = () => { clearStallTimer(); updateBuffered(); };
    const handleEnded = () => { setIsPlaying(false); onCompleteRef.current?.(); };
    const handleWaiting = () => {
      setIsLoading(true);
      // A `waiting` fired mid-seek is just seek-buffering, NOT a CDN stall. Don't
      // arm the fallback timer — swapping the src mid-seek freezes playback. A
      // genuine stall after the seek resolves re-arms via the next `waiting`
      // (video.seeking === false); the countdown is also cleared on `seeked`.
      if (video.seeking) return;
      // If a CDN source (the primary, or a compat hop) stalls past
      // STALL_TO_FALLBACK_MS, swap to the proxy fallback.
      if (fallbackUrlRef.current && fallbackPathRef.current !== 'proxy') {
        const onCompatHop = fallbackTriedRef.current;
        if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
        stallStartedAtRef.current = Date.now();
        stallTimerRef.current = setTimeout(() => {
          stallTimerRef.current = null;
          const elapsed = stallStartedAtRef.current
            ? Date.now() - stallStartedAtRef.current
            : 0;
          emitTelemetry({
            eventType: 'stall_detected',
            positionSec: video.currentTime || 0,
            stallDurationMs: elapsed,
            viaFallback: onCompatHop,
          });
          triggerFallback('stall');
        }, STALL_TO_FALLBACK_MS);
      } else if (fallbackTriedRef.current) {
        // Proxy URL is also stalling. First time: re-issue the same URL with
        // a cache-bust to recover transient mid-stream blips. Second time:
        // emit fallback_failed.
        if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
        stallStartedAtRef.current = Date.now();
        stallTimerRef.current = setTimeout(() => {
          stallTimerRef.current = null;
          const elapsed = stallStartedAtRef.current
            ? Date.now() - stallStartedAtRef.current
            : 0;
          if (
            !proxyRetriedRef.current &&
            fallbackPathRef.current === 'proxy' &&
            fallbackUrlRef.current
          ) {
            proxyRetriedRef.current = true;
            if (isFinite(video.currentTime) && video.currentTime > 0) {
              resumeTimeRef.current = video.currentTime;
            }
            const base = fallbackUrlRef.current;
            const retryUrl = base + (base.includes('?') ? '&' : '?') + 'retry=1';
            httpProtocolRef.current = '';
            console.warn('[VideoPlayer] proxy stalled, retrying once', {
              mediaId: mediaIdRef.current,
              positionSec: video.currentTime || 0,
            });
            setResolvedUrl(retryUrl);
            return;
          }
          emitTelemetry({
            eventType: 'fallback_failed',
            reason: 'stall',
            positionSec: video.currentTime || 0,
            stallDurationMs: elapsed,
            viaFallback: true,
            ...diagnosticSnapshot(),
          });
          // Both primary and proxy stalled out. Stop the spinner and surface a
          // recoverable error instead of spinning forever (the "stuck" state).
          setIsLoading(false);
          setError(t('videoPlayer.failedToLoad'));
          setErrorDetail(t('videoPlayer.errorDetail', { status: 'stall', mediaId: mediaId || 'src' }));
        }, STALL_AFTER_FALLBACK_MS);
      }
    };
    const handleCanPlay = () => {
      clearStallTimer();
      setIsLoading(false);
      video.playbackRate = playbackRateRef.current;
    };
    const handlePlaying = () => {
      clearStallTimer();
      setIsLoading(false);
      setIsPlaying(true);
      if (fallbackTriedRef.current && !reportedRecoveryRef.current) {
        reportedRecoveryRef.current = true;
        const stallDurationMs = stallStartedAtRef.current
          ? Date.now() - stallStartedAtRef.current
          : 0;
        emitTelemetry({
          eventType: 'playback_recovered',
          positionSec: video.currentTime || 0,
          stallDurationMs,
          viaFallback: true,
          ...diagnosticSnapshot(),
        });
        stallStartedAtRef.current = null;
      }
    };
    const handlePause = () => setIsPlaying(false);
    const handlePlay = () => setIsPlaying(true);
    const handleError = () => {
      const code = video.error?.code || 0;
      const msg = video.error?.message || '';
      if (triggerFallback('error', code)) return;
      const fallbackWasTried = fallbackTriedRef.current;
      const diag = diagnosticSnapshot();
      if (fallbackWasTried) {
        emitTelemetry({
          eventType: 'fallback_failed',
          reason: 'error',
          errorCode: code,
          errorMessage: msg,
          positionSec: video.currentTime || 0,
          viaFallback: true,
          ...diag,
        });
      }
      emitTelemetry({
        eventType: 'playback_failed',
        reason: 'error',
        errorCode: code,
        errorMessage: msg,
        positionSec: video.currentTime || 0,
        viaFallback: fallbackWasTried,
        ...diag,
      });
      setError(t('videoPlayer.failedToLoad'));
      const codeLabel = code ? `MediaError ${code}` : 'MediaError';
      setErrorDetail(t('videoPlayer.errorDetail', { status: codeLabel, mediaId: mediaId || 'src' }));
      console.warn('[VideoPlayer] video element error', { mediaId, code, message: video.error?.message });
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('durationchange', handleDurationChange);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('seeking', handleSeeking);
    video.addEventListener('seeked', handleSeeked);
    video.addEventListener('progress', handleProgress);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('playing', handlePlaying);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('error', handleError);

    return () => {
      clearStallTimer();
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('durationchange', handleDurationChange);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('seeking', handleSeeking);
      video.removeEventListener('seeked', handleSeeked);
      video.removeEventListener('progress', handleProgress);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('playing', handlePlaying);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('error', handleError);
      videoTelemetry.flushNow(true);
    };
  }, [videoUrl, triggerFallback, clearStallTimer, emitTelemetry, diagnosticSnapshot]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) { video.play(); } else { video.pause(); }
  }, []);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !isMuted;
    setIsMuted(!isMuted);
  }, [isMuted]);

  const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const video = videoRef.current;
    const bar = progressRef.current;
    if (!video || !bar || duration <= 0) return;

    const rect = bar.getBoundingClientRect();
    const pos = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const targetTime = pos * duration;

    // CloudFront serves static MP4 with faststart -- browser handles byte-range natively
    video.currentTime = targetTime;
  }, [duration]);

  const toggleFullscreen = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      container.requestFullscreen();
    }
  }, []);

  const changeSpeed = useCallback((rate: number) => {
    const video = videoRef.current;
    if (video) {
      video.playbackRate = rate;
      if (!video.paused) { video.play().catch(() => {}); }
    }
    playbackRateRef.current = rate;
    setPlaybackRate(rate);
    setShowSpeedMenu(false);
    localStorage.setItem('videoPlaybackRate', String(rate));
  }, []);

  // Close speed menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (speedMenuRef.current && !speedMenuRef.current.contains(e.target as Node)) {
        setShowSpeedMenu(false);
      }
    };
    if (showSpeedMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSpeedMenu]);

  const handleMouseMove = useCallback(() => {
    setShowControls(true);
    if (hideControlsTimeout.current) {
      clearTimeout(hideControlsTimeout.current);
    }
    hideControlsTimeout.current = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false);
        setShowSpeedMenu(false);
      }
    }, 3000);
  }, [isPlaying]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const preventContextMenu = (e: React.MouseEvent) => e.preventDefault();

  if (error) {
    return (
      <div className={`${className} bg-gray-50 rounded-xl flex flex-col items-center justify-center aspect-video px-4 text-center`}>
        <p className="text-red-600">{error}</p>
        {errorDetail && (
          <p className="text-xs text-gray-400 mt-2 select-text">{errorDetail}</p>
        )}
      </div>
    );
  }

  if (!videoUrl && !mediaId) {
    return (
      <div className={`${className} bg-gray-50 rounded-xl flex items-center justify-center aspect-video`}>
        <p className="text-gray-500">{t('videoPlayer.noVideoAvailable')}</p>
      </div>
    );
  }

  const isCanvasMode = useCanvasRendering.current && watermarkRef.current;

  return (
    <div
      ref={containerRef}
      className={`${className} relative bg-black rounded-xl overflow-hidden group`}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => isPlaying && setShowControls(false)}
      onContextMenu={preventContextMenu}
    >
      {/* Hidden video element for canvas mode, visible for local mode */}
      <video
        ref={videoRef}
        src={videoUrl || undefined}
        poster={isCanvasMode ? undefined : poster}
        className={isCanvasMode ? 'absolute top-0 left-0 w-full h-full opacity-0 pointer-events-none' : 'w-full h-full'}
        autoPlay={autoPlay}
        loop={loop}
        muted={isMuted}
        onClick={isCanvasMode ? undefined : togglePlay}
        playsInline
        controlsList="nodownload"
        disablePictureInPicture
        onContextMenu={preventContextMenu}
        crossOrigin={isCanvasMode && videoUrl && videoUrl.startsWith('http') ? 'anonymous' : undefined}
      />

      {/* Canvas for watermarked rendering */}
      {isCanvasMode && (
        <canvas
          ref={canvasRef}
          className="w-full h-full"
          onClick={togglePlay}
          onContextMenu={preventContextMenu}
        />
      )}

      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <Loader2 className="w-10 h-10 text-gold animate-spin" />
        </div>
      )}

      {/* Play button overlay */}
      {!isPlaying && !isLoading && (
        <div
          className="absolute inset-0 flex items-center justify-center cursor-pointer"
          onClick={togglePlay}
        >
          <div className="p-4 bg-gold/90 rounded-full hover:bg-gold transition-colors">
            <Play className="w-8 h-8 text-black fill-current" />
          </div>
        </div>
      )}

      {/* Controls */}
      <div
        className={`
          absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent
          px-4 py-3 transition-opacity duration-300
          ${showControls ? 'opacity-100' : 'opacity-0'}
        `}
      >
        {/* Progress bar */}
        <div
          ref={progressRef}
          className="w-full h-1 bg-gray-600 rounded-full mb-3 cursor-pointer relative"
          onClick={handleSeek}
        >
          {/* Buffer bar */}
          <div
            className="absolute h-full bg-white/20 rounded-full"
            style={{ width: `${Math.min(buffered, 100)}%` }}
          />
          {/* Playback bar */}
          <div
            className="absolute h-full bg-gold rounded-full"
            style={{ width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%' }}
          />
        </div>

        {/* Control buttons */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={togglePlay}
              className="text-white hover:text-gold transition-colors"
            >
              {isPlaying ? (
                <Pause className="w-5 h-5" />
              ) : (
                <Play className="w-5 h-5" />
              )}
            </button>

            <button
              onClick={toggleMute}
              className="text-white hover:text-gold transition-colors"
            >
              {isMuted ? (
                <VolumeX className="w-5 h-5" />
              ) : (
                <Volume2 className="w-5 h-5" />
              )}
            </button>

            <span className="text-white text-sm">
              {formatTime(currentTime)}{duration > 0 ? ` / ${formatTime(duration)}` : ''}
            </span>
          </div>

          <div className="flex items-center gap-3">
            {/* Speed control */}
            <div ref={speedMenuRef} className="relative">
              <button
                onClick={() => setShowSpeedMenu(v => !v)}
                className="text-white hover:text-gold transition-colors text-sm font-medium flex items-center justify-center"
              >
                {playbackRate === 1 ? (
                  <Gauge className="w-5 h-5" />
                ) : (
                  <span>{playbackRate}x</span>
                )}
              </button>
              {showSpeedMenu && (
                <div className="absolute bottom-full right-0 mb-2 bg-black/90 rounded-lg py-1 min-w-[4rem]">
                  {[0.5, 0.75, 1, 1.25, 1.5, 2].map(rate => (
                    <button
                      key={rate}
                      onClick={() => changeSpeed(rate)}
                      className={`block w-full text-left px-3 py-1 text-sm transition-colors ${
                        rate === playbackRate
                          ? 'text-gold'
                          : 'text-white hover:text-gold'
                      }`}
                    >
                      {rate}x
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={toggleFullscreen}
              className="text-white hover:text-gold transition-colors"
            >
              <Maximize className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
